import { ChangeEventHandler, FocusEventHandler } from 'react'
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaLock,
  FaMapMarkedAlt,
  FaRoute,
  FaWallet
} from 'react-icons/fa'

import { Field } from 'formik'

import Button from 'components/button'

import * as S from '../../style'

const getYesterdayDate = () => {
  const date = new Date()
  date.setDate(date.getDate() - 1)

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-')
}

const emptyCity = () => ({
  id: '',
  name: '',
  description: '',
  latitude: 0,
  longitude: 0,
  images: [],
  accommodations: []
})

const buildDayPlan = (current: any[] = [], requestedDays: number) =>
  Array.from({ length: requestedDays }, (_, index) => {
    const existing = current[index]

    return (
      existing || {
        dayNumber: index + 1,
        title: '',
        date: null,
        cities: [emptyCity()]
      }
    )
  }).map((day, index) => ({
    ...day,
    dayNumber: index + 1,
    cities:
      Array.isArray(day.cities) && day.cities.length > 0
        ? day.cities
        : [emptyCity()]
  }))

type PainelFaceProps = {
  handleChange: ChangeEventHandler<HTMLInputElement>
  handleBlur: FocusEventHandler<HTMLInputElement>
  values: any
  setFieldValue: (field: string, value: any) => void
  className: string
  nextStep: () => void
}

const PainelFace = ({
  handleChange,
  handleBlur,
  values,
  setFieldValue,
  className,
  nextStep
}: PainelFaceProps) => {
  const made = values.made === true || String(values.made) === 'true'

  const chooseType = (isMade: boolean) => {
    setFieldValue('made', isMade ? 'true' : 'false')
    if (!isMade) setFieldValue('tripDate', '')
  }

  return (
    <S.PainelCreateItinerary className={className}>
      <S.BasicInfoWorkspace>
        <S.BasicInfoHeader>
          <S.BasicInfoEyebrow>
            <FaRoute />
            Etapa 1 de 3
          </S.BasicInfoEyebrow>
          <h2>Comece pelas informações da viagem</h2>
          <p>
            Defina como este roteiro será publicado e conte o essencial. No
            próximo passo você organiza os dias, cidades, fotos e experiências.
          </p>
        </S.BasicInfoHeader>

        <S.RouteTypeSection>
          <S.SectionTitle>
            <strong>Tipo de roteiro</strong>
            <span>
              Essa escolha define a visibilidade inicial do roteiro na
              RedSterna.
            </span>
          </S.SectionTitle>

          <S.RouteTypeGrid>
            <S.RouteTypeCard
              type="button"
              $active={made}
              onClick={() => chooseType(true)}
            >
              <S.RouteTypeTop>
                <S.RouteTypeIcon $active={made}>
                  <FaCheckCircle />
                </S.RouteTypeIcon>
                <div>
                  <strong>Feito</strong>
                  <span>Essa viagem já aconteceu</span>
                </div>
                <S.RouteTypeRadio $active={made} aria-hidden="true" />
              </S.RouteTypeTop>

              <S.RouteTypeVisibility $active={made}>
                <FaMapMarkedAlt />
                <div>
                  <strong>O roteiro ficará público no mapa</strong>
                  <span>
                    Outros viajantes poderão encontrar e usar sua experiência
                    como inspiração.
                  </span>
                </div>
              </S.RouteTypeVisibility>

              <Field
                className="sr-only"
                id="made_feito"
                name="made"
                value="true"
                type="radio"
                onChange={() => chooseType(true)}
              />
            </S.RouteTypeCard>

            <S.RouteTypeCard
              type="button"
              $active={!made}
              onClick={() => chooseType(false)}
            >
              <S.RouteTypeTop>
                <S.RouteTypeIcon $active={!made}>
                  <FaLock />
                </S.RouteTypeIcon>
                <div>
                  <strong>Planejado</strong>
                  <span>Essa viagem ainda vai acontecer</span>
                </div>
                <S.RouteTypeRadio $active={!made} aria-hidden="true" />
              </S.RouteTypeTop>

              <S.RouteTypeVisibility $active={!made}>
                <FaLock />
                <div>
                  <strong>O roteiro ficará privado</strong>
                  <span>
                    Ele fica visível apenas para você enquanto estiver
                    planejando.
                  </span>
                </div>
              </S.RouteTypeVisibility>

              <Field
                className="sr-only"
                id="made_planejado"
                name="made"
                value="false"
                type="radio"
                onChange={() => chooseType(false)}
              />
            </S.RouteTypeCard>
          </S.RouteTypeGrid>
        </S.RouteTypeSection>

        <S.BasicInfoCard>
          <S.BasicInfoGrid>
            <S.ModernField className="wide">
              <label htmlFor="title">
                <FaRoute />
                Título da viagem
              </label>
              <Field
                id="title"
                name="title"
                type="text"
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Ex.: Paris, Lyon e Provence em 10 dias"
                value={values.title}
              />
              <small>
                Dê um nome fácil de reconhecer. Você poderá alterar depois.
              </small>
            </S.ModernField>

            {made && (
              <S.ModernField>
                <label htmlFor="tripDate">
                  <FaCalendarAlt />
                  Data de início
                </label>
                <Field
                  id="tripDate"
                  name="tripDate"
                  type="date"
                  max={getYesterdayDate()}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.tripDate}
                />
                <small>Roteiros feitos precisam começar antes de hoje.</small>
              </S.ModernField>
            )}

            <S.ModernField>
              <label htmlFor="days">
                <FaClock />
                Quantos dias?
              </label>
              <Field
                id="days"
                name="days"
                type="number"
                min="1"
                max="365"
                value={values.days}
                onChange={(event: any) => {
                  const requestedDays = Math.max(
                    1,
                    Math.min(
                      365,
                      Number.parseInt(event.target.value || '1', 10) || 1
                    )
                  )

                  setFieldValue('days', requestedDays)
                  setFieldValue(
                    'dayPlan',
                    buildDayPlan(values.dayPlan || [], requestedDays)
                  )
                }}
                onBlur={handleBlur}
                placeholder="1"
              />
              <small>
                No próximo passo você poderá adicionar cidades em cada dia.
              </small>
            </S.ModernField>

            <S.ModernField>
              <label htmlFor="spent">
                <FaWallet />
                Custo total por pessoa
              </label>
              <S.CurrencyInput>
                <span>R$</span>
                <Field
                  id="spent"
                  name="spent"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="number"
                  placeholder="0,00"
                  step="0.01"
                  min="0"
                  value={values.spent}
                />
              </S.CurrencyInput>
              <small>Opcional. Informe uma estimativa em reais.</small>
            </S.ModernField>
          </S.BasicInfoGrid>
        </S.BasicInfoCard>
      </S.BasicInfoWorkspace>

      <S.ContentCreateItinerary>
        <Button type="button" onClick={nextStep}>
          <p>Continuar</p>
        </Button>
      </S.ContentCreateItinerary>
    </S.PainelCreateItinerary>
  )
}

export default PainelFace
